import { observable, action } from 'mobx'
import { observer } from 'mobx-react'
import React, { Component, Fragment } from 'react'
import Card from '../../components/Card'
import Header from '../../components/Header'
import Image from '../../components/Image'
import OpticNerveProgression from '../../components/OpticNerveProgression'
import Paginate from '../../components/Paginate'
import Paragraph from '../../components/Paragraph'
import PatientImages from '../../components/PatientImages'
import Prompts from '../../components/Prompts'
import SectionHeader from '../../components/SectionHeader'
import Video from '../../components/Video'
import VisualFieldProgression from '../../components/VisualFieldProgression'
import lang from '../../stores/lang'
import patient from '../../stores/patient'
import styled from 'styled-components'
import Slider from '../../components/Slider'
import BigImage from '../../components/BigImage'
import Row from '../../components/Row'
import OCT from '../../components/OCT'
import Input from '../../components/Input'
import Select from '../../components/Select'
import Toggle from '../../components/Toggle'
import Text from '../../components/Text'
import TextArea from '../../components/TextArea'

const Circle = styled.div`
  width: 3rem;
  height: 3rem;
  border-radius: 1.5rem;
  background: ${props => props.color};
  margin-bottom: 1.5rem;
  &:last-child {
    margin-bottom: 0;
  }
`

const TrafficWrap = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
`

const Box = styled.div`
  padding: 1.5rem;
  background: gray;
  border-radius: 1.5rem;
  margin-top: 1rem;
`

const TrafficExp = styled.div`
  line-height: 3rem;
  margin-bottom: 1.5rem;
  font-size: 1.5rem;
  &:last-child {
    margin-bottom: 0;
  }
`

function TrafficLight(prop) {
  return (
    <TrafficWrap>
      <Box>
        <Circle color="red" />
        <Circle color="yellow" />
        <Circle color="green" />
      </Box>
      <div style={{ marginLeft: '2rem', padding: '1rem' }}>
        <TrafficExp><Text str="prose.redTrafficLight" /></TrafficExp>
        <TrafficExp><Text str="prose.yellowTrafficLight" /></TrafficExp>
        <TrafficExp><Text str="prose.greenTrafficLight" /></TrafficExp>
      </div>
    </TrafficWrap>
  )
}

@observer
export default class Screening extends Component {
  @observable confidence = 5

  @action
  updateConfidence = value => {
    this.confidence = value
  }

  render() {
    // const demo = false
    // const session = "first"
    // const firstSession = true
    // const thirdSession = false
    // const patientID = patient.fields._id
    // const diagnosisText = lang.translate(`choices.diagnosis.${patient.fields.diagnosis}`)

    const octLeft = [patient.fields.octLeftS, patient.fields.octLeftN, patient.fields.octLeftI, patient.fields.octLeftT]
    const octRight = [patient.fields.octRightS, patient.fields.octRightN, patient.fields.octRightI, patient.fields.octRightT]

    const octLeftGreen = parseInt(octLeft.reduce((s, v) => v === 'GREEN' ? s + 1 : s, 0) / 4 * 100, 10)
    const octLeftYellow = parseInt(octLeft.reduce((s, v) => v === 'YELLOW' ? s + 1 : s, 0) / 4 * 100, 10)
    const octLeftRed = parseInt(octLeft.reduce((s, v) => v === 'RED' ? s + 1 : s, 0) / 4 * 100, 10)

    const octRightGreen = parseInt(octRight.reduce((s, v) => v === 'GREEN' ? s + 1 : s, 0) / 4 * 100, 10)
    const octRightYellow = parseInt(octRight.reduce((s, v) => v === 'YELLOW' ? s + 1 : s, 0) / 4 * 100, 10)
    const octRightRed = parseInt(octRight.reduce((s, v) => v === 'RED' ? s + 1 : s, 0) / 4 * 100, 10)

    const diagnosis = lang.translate(`choices.diagnosis.${patient.fields.diagnosis}`)

    return (
      <Fragment>
        {/* YOUR GLAUCOMA */}
        <Prompts>
          <Prompts.P>
            {patient.fields.lowVision === "YES" &&
              <Paragraph str="prose.lowVisionScreeningIntro" inserts={{diagnosis}}/>
            }
            {patient.fields.lowVision === "NO" &&
              <Paragraph str="prose.screeningIntro" inserts={{diagnosis}} />
            }
          </Prompts.P>
        </Prompts>
        <SectionHeader color="blue" icon="far fa-dot-circle" str="titles.yourGlaucoma" />
          <Prompts>
            <Prompts.Q>
              <Paragraph str="prose.tellMeAlreadyKnow"/>
              <Paragraph str="prose.heardAboutGlaucoma"/>
            </Prompts.Q>
            <Prompts.R str="prose.youKnowGlaucomaCan" />
            <Prompts.P>
              <Paragraph str="prose.shareMoreAboutDiagnosis" inserts={{diagnosis}}/>
            </Prompts.P>
          </Prompts>
        <Video screeningSession />
        <Prompts>
          {patient.fields.lowVision === "NO" &&
            <Prompts.Q>
              <Paragraph str="prose.understandingDiagnosis" inserts={{diagnosis}}/>
            </Prompts.Q>
          }
          {patient.fields.lowVision === "YES" &&
            <Prompts.Q>
              <Paragraph str="prose.lowVisionUnderstandingDiagnosis" inserts={{diagnosis}}/>
            </Prompts.Q>
          }
          <Prompts.R><Paragraph str="prose.reflect"/></Prompts.R>
          {patient.fields.lowVision === "NO" && <Prompts.P><Paragraph str={patient.fields.diagnosis === 'SUSPECT'? "prose.suspectRisk": "prose.nonSuspectRisk"} inserts={{diagnosis}}/></Prompts.P>}
          {patient.fields.lowVision === "YES" && <Prompts.P><Paragraph str={patient.fields.diagnosis === 'SUSPECT'? "prose.lowVisionSuspectRisk": "prose.lowVisionNonSuspectRisk"}/></Prompts.P>}
          <Prompts.R><Paragraph str="prose.reflectFirstThenAsked"/></Prompts.R>
        </Prompts>
        {/* EXAM RESULTS */}
        <SectionHeader color="blue" icon="fas fa-eye" str="titles.examResults" />
          <Prompts>
            <Prompts.P str="prose.ifItsOkayOpticNerve" />
          </Prompts>
        {/* EXAM RESULTS - THE OPTIC NERVE */}
        <Header str="titles.theOpticNerve" />
        <Card>
          <Card.Pad>
            <Image src="/images/donuts.png" />
            <Paragraph str="prose.screeningDonuts" />
            {/* <Paragraph str="prose.donuts" /> */}
          </Card.Pad>
        </Card>
        {patient.hasOpticNerves && patient.fields.lowVision === "NO" &&
          <Fragment>
            {/* EXAM RESULTS - YOUR OPTIC NERVES */}
            <Header str="titles.yourOpticNerves" />
            <Card>
              <Card.Pad>
                <Paragraph str="prose.firstTestResultWeWill" />
              </Card.Pad>
            </Card>
            <PatientImages
              left={patient.fields.leftOpticNerve}
              leftDate={patient.fields.leftOpticNerveDate}
              right={patient.fields.rightOpticNerve}
              rightDate={patient.fields.rightOpticNerveDate}
            />
          </Fragment>
        }
        {patient.fields.lowVision === "NO" && <Fragment>
          <Header str="titles.opticNerveProgression" />
          <Prompts>
            <Prompts.P><Paragraph str="prose.cupChange" /></Prompts.P>
          </Prompts>
          <OpticNerveProgression />
        </Fragment>}
        <Prompts>
          {patient.fields.lowVision === "NO" && <Fragment>
            <Prompts.Q><Paragraph str="prose.whatNoticeInVideo" /></Prompts.Q>
            <Prompts.R><Paragraph str="prose.reflectYouDotDotDot" /></Prompts.R>
          </Fragment>}
          <Prompts.P>
            <Paragraph str={patient.fields.lowVision === "NO"? "prose.talkAboutOctTest": "prose.lowVisionTalkAboutOctTest"} />
          </Prompts.P>
        </Prompts>
        <Header str="titles.octTest" />
        <Card>
          <Card.Pad>
            <Paragraph str="prose.octTest"/>
          </Card.Pad>
        </Card>
        <Card>
          <Card.Pad>
            <Paragraph centered style={{fontSize: '1.5rem'}} str="prose.trafficLight"/>
            {/* <Row>
              <Row.Cell><TrafficLight /></Row.Cell>
              <Row.Cell>
                <Paragraph>Red an area of damaged tissue</Paragraph>
                <Paragraph>Yellow means an area of possible concern</Paragraph>
                <Paragraph>Green means an area of healthy tissue</Paragraph>
              </Row.Cell>
            </Row> */}
            <TrafficLight />
          </Card.Pad>
        </Card>
        <Header str="titles.yourTestResults" />
        {patient.fields.lowVision === "YES" && <Prompts>
          <Prompts.P>
            <Paragraph str="prose.lowVisionOctTestResults" inserts={{
              octLeftGreen, octLeftYellow, octLeftRed, octRightGreen, octRightYellow, octRightRed
            }} />
          </Prompts.P>
        </Prompts>}
        <Card>
          <Card.Pad>
            <Text str="titles.date"/>: {patient.fields.octDate || 'N/A'}
            <Row>
              <Row.Cell centered><OCT eye="right" /></Row.Cell>
              <Row.Cell centered><OCT eye="left" /></Row.Cell>
            </Row>
            <Row>
              <Row.Cell centered><span style={{fontSize: '0.9rem', color: '#888'}}><Text str="titles.rightEye"/></span></Row.Cell>
              <Row.Cell centered><span style={{fontSize: '0.9rem', color: '#888'}}><Text str="titles.leftEye"/></span></Row.Cell>
            </Row>
          </Card.Pad>
          {!!patient.fields.screeningReasons.length &&
            <Fragment>
              <Card.Divider />
              <Card.Pad>
                <Paragraph str="prose.evenIfTestNormal" />
                {patient.fields.screeningReasons.map(code => (
                  <Toggle
                    color="dark-blue"
                    str={`choices.screeningReason.forPatient.${code}`}
                    style={{ cursor: "default" }}
                    checked
                    disabled
                  />
                ))}
              </Card.Pad>
            </Fragment>
          }
        </Card>
        {/* <PatientImages
          left="/screening/oct-left.png"
          leftDate="January 1, 2019"
          right="/screening/oct-right.png"
          rightDate="January 1, 2019"
        /> */}
        <Prompts>
          <Prompts.P><Paragraph str="prose.questionAboutTestResults"/></Prompts.P>
          <Prompts.R><Paragraph str="prose.youDotDotDot"/></Prompts.R>
        </Prompts>
        {/* VISUAL FIELD PROGRESSION */}
        <SectionHeader color="blue" icon="fas fa-hourglass-half" str="titles.visualFieldProgression" />
        <Prompts>
          <Prompts.Q><Paragraph str="prose.visualFieldTest"/></Prompts.Q>
        </Prompts>
        <Card>
          <Card.Pad>
            {patient.fields.lowVision === "NO" && <Fragment>
              <Paragraph str="prose.meetDoctorVisualFieldTest" />
              <Paragraph str="prose.showVideoAffectVision" />
              <Paragraph str="prose.whySerious" />
            </Fragment>}
            {patient.fields.lowVision === "YES" && <Fragment>
              <Paragraph str="prose.lowVisionMeetDoctorVisualFieldTest" />
              <Paragraph str="prose.controlGlaucoma" />
            </Fragment>}
          </Card.Pad>
        </Card>
        {patient.fields.lowVision === "NO" && <Fragment>
          <VisualFieldProgression screening />
          <Prompts>
            <Prompts.Q str="prose.peopleFindThisScary" />
            <Prompts.R><Text str="prose.youFeel"/></Prompts.R>
            <Prompts.R><Text str="prose.maintainVision" /></Prompts.R>
          </Prompts>
        </Fragment>}
        {/* TREATMENT PLAN */}
        <SectionHeader color="blue" icon="fas fa-search-plus" str="titles.importanceOfFollowUpCare" />
        <Card>
          <Card.Pad>
            {patient.fields.diagnosis === 'SUSPECT' &&
              <Row>
                <Row.Cell>
                  <Paragraph str="prose.suspectFollowUpParagraph1" />
                  <Paragraph>
                    <Text str="prose.suspectFollowUpParagraph2"/> <b><Text str="prose.yearsOld" inserts={{age: (patient.age-10)>40?40:Math.max(patient.age-10, 0)}}/></b>
                  </Paragraph>
                </Row.Cell>
                <Row.Cell><Image src={`/screening/${lang.translatePeopleChart()}`} /></Row.Cell>
              </Row>
            }
            {patient.fields.diagnosis !== 'SUSPECT' && <Fragment>
              <Paragraph str="prose.followUpParagraph1" />
              <Paragraph>
                <Text str="prose.followUpParagraph2" inserts={{diagnosis: lang.translate(`choices.diagnosis.${patient.fields.diagnosis}`)}} /> <b><Text str="prose.yearsOld" inserts={{age: (patient.age-10)>40?40:Math.max(patient.age-10, 0)}}/></b>
              </Paragraph>
            </Fragment>}
          </Card.Pad>
        </Card>
        <Fragment>
          {/* BARRIERS */}
          <SectionHeader color="blue" icon="fas fa-file-alt" str="titles.barriers" />
          <Prompts>
            <Prompts.Q><Paragraph str="prose.questionsConcerns"/></Prompts.Q>
            <Prompts.R><Paragraph str="prose.reflectEmotion"/></Prompts.R>
            <Prompts.Q><Paragraph str="prose.getDoctorAppointment"/></Prompts.Q>
            <Prompts.R><Paragraph str="prose.itSoundsLike"/></Prompts.R>
            <Prompts.Q><Paragraph str="prose.planB"/></Prompts.Q>
            <Prompts.R><Paragraph str="prose.reflectBackIdeas"/></Prompts.R>
            <Prompts.Q><Paragraph str="prose.offerSuggestions"/></Prompts.Q>
            <Prompts.R><Paragraph str="prose.reflect"/></Prompts.R>
            <Prompts.Q><Paragraph str="prose.forgotAppointment"/></Prompts.Q>
            <Prompts.R><Paragraph str="prose.reflect"/></Prompts.R>
            <Prompts.Q><Paragraph str="prose.helpToGetToAppointment"/></Prompts.Q>
            <Prompts.R><Paragraph str="prose.reflect"/></Prompts.R>
            <Prompts.Q><Paragraph str="prose.additionalIdeas"/></Prompts.Q>
            <Prompts.R><Paragraph str="prose.summarizeBarriers"/></Prompts.R>
          </Prompts>
          <BigImage src="/images/family-hugging.jpg" />
          <Card>
            <Card.Title str="titles.howConfidentKeepAppointment" />
            <Card.Pad>
              <Slider
                min={1}
                max={10}
                pinColor="blue"
                value={this.confidence}
                onChange={this.updateConfidence}
              />
            </Card.Pad>
          </Card>
          <Prompts>
            <Prompts.Q><Paragraph str="prose.curiousAboutConfidence"/></Prompts.Q>
            <Prompts.Q>
                {this.confidence > 2 && <Paragraph str="prose.confidenceMoreThan2"
                                                   inserts={{confidence: this.confidence, confidenceMinusTwo: this.confidence - 2}}/>}
                {this.confidence === 2 && <Paragraph str="prose.confidenceEqual2"/>}
                {this.confidence === 1 && <Paragraph str="prose.confidenceEqual1"/>}
            </Prompts.Q>
            <Prompts.R><Paragraph str="prose.itSoundsLike"/></Prompts.R>
            <Prompts.Q>
                {this.confidence <= 9 && <Paragraph str="prose.confidenceLessEqual9"
                                                    inserts={{confidence: this.confidence, confidencePlusTwo: Math.min(this.confidence + 2, 10)}}/>}
                {this.confidence === 10 && <Paragraph str="prose.confidenceEqual10"/>}
            </Prompts.Q>
            <Prompts.R><Paragraph str="prose.ideaImproveConfidence"/></Prompts.R>
          </Prompts>
        </Fragment>
        <SectionHeader color="blue" icon="fas fa-edit" str="titles.plan" />
        <Prompts>
          <Prompts.P>
            <Paragraph str={patient.fields.lowVision === "NO"?"prose.wrapUpPlan":"prose.lowVisionWrapUpPlan"}/>
          </Prompts.P>
          <Prompts.Q><Paragraph str="prose.whatMissed"/></Prompts.Q>
          <Prompts.R><Paragraph str="prose.reflect"/></Prompts.R>
          <Prompts.Q><Paragraph str="prose.scheduleToday"/></Prompts.Q>
          <Prompts.Q>
            <Paragraph str="prose.whatTime"/>
            <Paragraph str="prose.readQuestions"/>
          </Prompts.Q>
        </Prompts>
        <Card>
          <Card.Title str="titles.appointmentSummary" />
          <Card.Pad>
            <Paragraph str="prose.mayHaveGlaucoma" />
            {patient.fields.lowVision === "NO" && <Paragraph str="prose.maintainVisionImportant"/>}
            <Row alignCenter>
              <Row.Cell flex="1" centered><Paragraph str="prose.youPlanTo"/></Row.Cell>
              <Row.Cell flex="4" centered>
                {/* <Input name="screeningSessionSummaryPlan" type="text" onChange={patient.setField} value={patient.fields.screeningSessionSummaryPlan} /> */}
                <TextArea
                  value={patient.fields.screeningSessionSummaryPlan}
                  name="screeningSessionSummaryPlan"
                  onChange={patient.setField}
                ></TextArea>
                </Row.Cell>
            </Row>
            <Row alignCenter>
              <Row.Cell flex="1" centered><Paragraph str="prose.appointmentScheduledFor"/></Row.Cell>
              <Row.Cell flex="1" centered><Input name="screeningSessionSummaryApptDate" type="text" onChange={patient.setField} value={patient.fields.screeningSessionSummaryApptDate} /></Row.Cell>
            </Row>
            <Row alignCenter>
              <Row alignCenter>
                <Row.Cell centered flex="1"><Paragraph str="titles.location"/>:</Row.Cell>
                <Row.Cell flex="4">
                  <Select name="screeningSessionSummaryProviderLocation" onChange={patient.setField} allowBlank value={patient.fields.screeningSessionSummaryProviderLocation}>
                    <Select.Options choices="providerLocation" />
                  </Select>
                </Row.Cell>
              </Row>
            </Row>
            <Row alignCenter>
              <Row.Cell centered>{
                (patient.fields.screeningSessionSummaryProviderLocation === 'KELLOGG_EYE_CENTER' &&<b>1000 Wall St, Ann Arbor, MI 48105</b>) ||
                (patient.fields.screeningSessionSummaryProviderLocation === 'HAMILTON_CLINIC' && <b>2900 N. Saginaw Street, Flint, MI 48505</b>) ||
                (patient.fields.screeningSessionSummaryProviderLocation === 'HOPE_CLINIC' && <b>518 Harriet Street, Ypsilanti, MI 48197</b>) ||
                (patient.fields.screeningSessionSummaryProviderLocation === 'OTHER' &&
                  <Paragraph str="prose.typeProviderAddress" />)
            }</Row.Cell>
            </Row>
            {patient.fields.screeningSessionSummaryProviderLocation === 'OTHER' &&
              <Fragment>
                <Input
                  type="text"
                  placeholder="Address line 1"
                  name="screeningSessionSummaryProviderAddr1"
                  value={patient.fields.screeningSessionSummaryProviderAddr1 || ''}
                  onChange={patient.setField}
                />
                <Input
                  type="text"
                  placeholder="Address line 2"
                  name="screeningSessionSummaryProviderAddr2"
                  value={patient.fields.screeningSessionSummaryProviderAddr2 || ''}
                  onChange={patient.setField}
                />
              </Fragment>
            }
            <Row alignCenter>
              <Row.Cell centered flex="1"><Paragraph str="prose.needToCancel"/></Row.Cell>
              <Row.Cell>
                <Input
                  type="text"
                  name="screeningSessionSummaryProviderPhone"
                  placeholder={
                    (patient.fields.screeningSessionSummaryProviderLocation === 'OTHER' && 'Provider phone number') ||
                    (!patient.fields.screeningSessionSummaryProviderLocation && 'Please select a provider location') ||
                    ''
                  }
                  value={
                    (patient.fields.screeningSessionSummaryProviderLocation === 'KELLOGG_EYE_CENTER' && '(734) 436-1186') ||
                    (patient.fields.screeningSessionSummaryProviderLocation === 'HAMILTON_CLINIC' && '(810) 309-9378') ||
                    (patient.fields.screeningSessionSummaryProviderLocation === 'HOPE_CLINIC' && '(734) 436-1186') ||
                    (patient.fields.screeningSessionSummaryProviderLocation === 'OTHER' && (patient.fields.screeningSessionSummaryProviderPhone || '')) ||
                    ''
                  }
                  onChange={patient.fields.screeningSessionSummaryProviderLocation === 'OTHER' ? patient.setField : () => {}}
                  disabled={patient.fields.screeningSessionSummaryProviderLocation !== 'OTHER'}
                />
              </Row.Cell>
            </Row>
          </Card.Pad>
        </Card>
        <Prompts>
          <Prompts.P>
            <Paragraph str="prose.printNotes" />
          </Prompts.P>
          <Prompts.P><Paragraph str="prose.thankScreening"/></Prompts.P>
        </Prompts>
        <Paginate>
          <Paginate.Button color="blue" str="titles.printPatientNotes" to={`/print/${patient.fields._id}/screening`} newWindow />
        </Paginate>
      </Fragment>
    )
  }
}



// WEBPACK FOOTER //
// ./src/pages/Screening/ScreeningContent.js